// Score severity constants and helper functions for audit scoring

export type ScoreSeverity = 'critical' | 'warning' | 'info' | 'none';

export interface ScoreStatus {
  severity: ScoreSeverity;
  label: string;
  colorClass: string;
  bgClass: string;
  emoji: string;
  requiresNotification: boolean;
  notifyOrgManager: boolean; // Only true for critical severity
}

// Score thresholds
export const SCORE_THRESHOLDS = {
  CRITICAL_MAX: 25,    // 0-25% - Contact Home Manager (red)
  WARNING_MAX: 40,     // 26-40% - Immediate improvements required (yellow)
  INFO_MAX: 65,        // 41-65% - Share improvements with team (green)
} as const;

/**
 * Determine score status based on percentage (0-100)
 * Returns severity, label, color classes, and notification requirements
 */
export function getScoreStatus(score: number): ScoreStatus {
  if (score <= SCORE_THRESHOLDS.CRITICAL_MAX) {
    return {
      severity: 'critical',
      label: 'Contact Home Manager',
      colorClass: 'text-red-600 dark:text-red-400',
      bgClass: 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800',
      emoji: '🟥',
      requiresNotification: true,
      notifyOrgManager: true, // Critical scores notify BOTH home manager AND org manager
    };
  }
  
  if (score <= SCORE_THRESHOLDS.WARNING_MAX) {
    return {
      severity: 'warning',
      label: 'Immediate improvements required',
      colorClass: 'text-yellow-600 dark:text-yellow-400',
      bgClass: 'bg-yellow-50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800',
      emoji: '🟨',
      requiresNotification: true,
      notifyOrgManager: false, // Warning only notifies home manager
    };
  }
  
  if (score <= SCORE_THRESHOLDS.INFO_MAX) {
    return {
      severity: 'info',
      label: 'Ensure improvements are shared with team',
      colorClass: 'text-green-600 dark:text-green-400',
      bgClass: 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800',
      emoji: '🟩',
      requiresNotification: true,
      notifyOrgManager: false, // Info only notifies home manager
    };
  }
  
  // Scores above 65% don't require notifications
  return {
    severity: 'none',
    label: 'Excellent',
    colorClass: 'text-blue-600 dark:text-blue-400',
    bgClass: 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800',
    emoji: '✅',
    requiresNotification: false,
    notifyOrgManager: false,
  };
}

/**
 * Generate notification message for a given audit and score
 */
export function generateNotificationMessage(auditName: string, score: number): string {
  return `The "${auditName}" has a ${score}% score. Please review.`;
}

/**
 * Get audit URL for notification click-through
 * For submitted/approved audits, use the view page
 * For draft audits, use the review/edit page
 */
export function getAuditUrl(auditId: number, status?: string): string {
  if (status === 'Submitted' || status === 'Approved') {
    return `/audits/view?auditId=${auditId}`;
  }
  // Use the canonical edit/take page (opens the audit edit form)
  return `/audits/take?auditId=${auditId}`;
}
